import { useState, type FormEvent } from "react";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { jobSeekersData } from "@/mocks/homeData";

export default function JobSeekersSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const { ref, isVisible } = useScrollAnimation(0.08);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const name = (formData.get("name") as string || "").trim();
    const email = (formData.get("email") as string || "").trim();
    const phone = (formData.get("phone") as string || "").trim();
    const experience = (formData.get("experience") as string || "").trim();
    const skills = (formData.get("skills") as string || "").trim();
    const roleType = (formData.get("role_type") as string || "").trim();

    if (!name || !email || !skills) {
      setStatus("error");
      setErrorMsg("Please fill in all required fields.");
      return;
    }

    const payload = new URLSearchParams();
    payload.append("name", name);
    payload.append("email", email);
    payload.append("phone", phone);
    payload.append("experience", experience);
    payload.append("skills", skills);
    payload.append("role_type", roleType);

    setStatus("loading");
    setErrorMsg("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9nobkkit35ii3jdltug", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const code = typeof parsed?.code === "string" ? parsed.code : "";
      const meta = (parsed as Record<string, unknown>)?.meta as Record<string, unknown> | undefined;
      const serverMsg = typeof meta?.message === "string" ? meta.message : "";
      const serverDetail = typeof meta?.detail === "string" ? meta.detail : "";
      const msg = serverMsg || serverDetail || responseText;
      const isSpam = msg.toLowerCase().includes("spam") || msg.toLowerCase().includes("form data is spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setErrorMsg(msg || "Something went wrong. Please try again.");
        return;
      }

      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Network error. Please try again.");
    }
  };

  return (
    <section id="careers" className="w-full py-16 md:py-24 bg-background-50">
      <div
        ref={ref}
        className={`w-full px-4 md:px-6 lg:px-10 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 max-w-6xl mx-auto">
          <div className="w-full lg:w-1/2">
            <span className="text-xs md:text-sm font-semibold text-primary-500 uppercase tracking-widest">
              {jobSeekersData.label}
            </span>
            <h2 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-foreground-950 mt-3 mb-4 leading-tight text-balance">
              {jobSeekersData.headline}
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">
              {jobSeekersData.subheadline}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {jobSeekersData.benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/70"
                >
                  <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center mb-3">
                    <i className={`${benefit.icon} text-lg text-primary-500`} />
                  </div>
                  <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-1.5">
                    {benefit.title}
                  </h4>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-950 mb-2">
                Join Our Talent Network
              </h3>
              <p className="text-sm text-foreground-600 mb-6">
                {jobSeekersData.formNote}
              </p>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
              >
                <div className="honeypot-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="career-name" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Full Name <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="career-name" type="text" name="name" required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="career-email" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Email <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="career-email" type="email" name="email" required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="you@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="career-phone" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Phone
                    </label>
                    <input
                      id="career-phone" type="tel" name="phone"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label htmlFor="career-experience" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Years of Experience
                    </label>
                    <select
                      id="career-experience" name="experience"
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    >
                      <option value="">Select range</option>
                      <option value="0-2">0-2 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="6-10">6-10 years</option>
                      <option value="11+">11+ years</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="career-role-type" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Preferred Role Type
                  </label>
                  <select
                    id="career-role-type" name="role_type"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  >
                    <option value="">Select type</option>
                    <option value="contract">Contract / Project-based</option>
                    <option value="permanent">Permanent / Full-time</option>
                    <option value="executive">Executive / Leadership</option>
                    <option value="training">Training & Placement</option>
                    <option value="open">Open to opportunities</option>
                  </select>
                </div>

                <div className="mb-5">
                  <label htmlFor="career-skills" className="block text-xs font-semibold text-foreground-700 mb-1.5">
                    Key Skills &amp; Technologies <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="career-skills" name="skills" required rows={3}
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                    placeholder="e.g., Java, React, AWS, Compliance Recording, Kubernetes..."
                  />
                </div>

                {status === "error" && errorMsg && (
                  <div className="mb-4 p-3 rounded-md bg-accent-50 border border-accent-200 text-accent-800 text-sm">
                    {errorMsg}
                  </div>
                )}
                {status === "success" && (
                  <div className="mb-4 p-3 rounded-md bg-primary-50 border border-primary-200 text-primary-800 text-sm">
                    Thank you. A RedeemSoft recruiter will review your profile and reach out within 48 hours.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full sm:w-auto px-8 py-3 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === "loading" ? "Submitting..." : "Submit Resume"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}